import React, { useState, useEffect } from 'react';
import {
  Button,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Switch,
  Tabs,
  Tag,
  Space,
  Popconfirm,
  message,
  Card,
  Row,
  Col,
  Statistic,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  BookOutlined,
  SafetyOutlined,
  ToolOutlined,
  ExperimentOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import {
  create_training_course,
  get_training_courses,
  update_training_course,
  delete_training_course,
  create_operation_requirement,
  get_operation_requirements,
} from '../../../utils/training_api';
import { bp_get_operation_type } from '../../../utils/api';
import StatCard from '@/components/StatCard';
import TableContainer from '@/components/TableContainer';
import { BookOpen, CheckCircle, Settings, Award } from 'lucide-react';
import useTableConfig from '@/hooks/useTableConfig';
import TableColumnConfig from '@/components/TableColumnConfig';
import ReusableSearchInput from '@/components/ReusableSearch';
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';
import Icons from '@/components/third-party/Icons';
import { useTranslation } from 'react-i18next';
import { Typography } from 'antd';

const { Option } = Select;
const { Title } = Typography;
const { TextArea } = Input;

const TrainingConfiguration = () => {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const textColor = isDark ? '#ffffff' : '#1f2937';

  // State for Training Courses
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [courseModalVisible, setCourseModalVisible] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [courseForm] = Form.useForm();

  // State for Operation Requirements
  const [requirements, setRequirements] = useState([]);
  const [requirementsLoading, setRequirementsLoading] = useState(false);
  const [requirementModalVisible, setRequirementModalVisible] = useState(false);
  const [operations, setOperations] = useState([]);
  const [requirementForm] = Form.useForm();

  // Statistics
  const [stats, setStats] = useState({
    totalCourses: 0,
    activeCourses: 0,
    operationsWithRequirements: 0,
  });

  // Search states
  const [courseSearchValue, setCourseSearchValue] = useState('');
  const [requirementSearchValue, setRequirementSearchValue] = useState('');

  // Training type options
  const trainingTypes = [
    { value: 'GMP', label: 'GMP (Good Manufacturing Practice)', icon: <SafetyOutlined /> },
    { value: 'SOP', label: 'SOP (Standard Operating Procedure)', icon: <FileTextOutlined /> },
    { value: 'EQUIPMENT', label: 'Equipment Training', icon: <ToolOutlined /> },
    { value: 'PRODUCT', label: 'Product Training', icon: <ExperimentOutlined /> },
    { value: 'SAFETY', label: 'Safety Training', icon: <SafetyOutlined /> },
    { value: 'QUALITY', label: 'Quality Training', icon: <BookOutlined /> },
    { value: 'OTHER', label: 'Other', icon: <FileTextOutlined /> },
  ];

  useEffect(() => {
    fetchCourses();
    fetchRequirements();
    fetchOperations();
  }, []);

  // Fetch training courses
  const fetchCourses = async () => {
    setCoursesLoading(true);
    try {
      await get_training_courses(null, (res) => {
        if (res?.status === 'success') {
          setCourses(res.data || []);
          updateStats(res.data || []);
        }
      });
    } catch (error) {
      message.error('Failed to fetch training courses');
    } finally {
      setCoursesLoading(false);
    }
  };

  // Fetch operation requirements
  const fetchRequirements = async () => {
    setRequirementsLoading(true);
    try {
      await get_operation_requirements(null, (res) => {
        if (res?.status === 'success') {
          setRequirements(res.data || []);
        }
      });
    } catch (error) {
      message.error('Failed to fetch operation requirements');
    } finally {
      setRequirementsLoading(false);
    }
  };

  // Fetch operation types
  const fetchOperations = async () => {
    try {
      await bp_get_operation_type((res) => {
        console.log('API Response:', res);
        console.log('API Data:', res?.data);
        if (res?.data?.status === 'success') {
          const rawData = res.data.data || [];
          console.log('Raw operation types count:', rawData.length);
          console.log('First 5 operations:', rawData.slice(0, 5));

          // Map operation types to expected format with new comprehensive fields
          const mapped = rawData.map((op) => ({
            operation_code: op.code || op.operation_type, // Use code field, fallback to operation_type
            operation_name: op.operation_type,
            id: op.operation_type_id,
            category: op.category,
            description: op.description,
            is_critical: op.is_critical,
            required_equipment_type: op.required_equipment_type,
            typical_duration_hours: op.typical_duration_hours,
          }));
          console.log('Mapped operations count:', mapped.length);
          console.log('First 5 mapped:', mapped.slice(0, 5));
          setOperations(mapped);
        } else {
          console.error('API returned non-success status:', res?.data?.status);
        }
      });
    } catch (error) {
      console.error('Failed to fetch operation types:', error);
    }
  };

  // Update statistics
  const updateStats = (coursesData) => {
    const activeCourses = coursesData.filter((c) => c.is_active).length;
    const uniqueOperations = new Set(requirements.map((r) => r.operation_code)).size;

    setStats({
      totalCourses: coursesData.length,
      activeCourses,
      operationsWithRequirements: uniqueOperations,
    });
  };

  // Handle create/update course
  const handleSaveCourse = async () => {
    try {
      const values = await courseForm.validateFields();

      if (editingCourse) {
        await update_training_course({ ...values, course_id: editingCourse.id }, (res) => {
          if (res?.status === 'success') {
            message.success('Training course updated successfully');
            setCourseModalVisible(false);
            courseForm.resetFields();
            setEditingCourse(null);
            fetchCourses();
          }
        });
      } else {
        await create_training_course(values, (res) => {
          if (res?.status === 'success') {
            message.success('Training course created successfully');
            setCourseModalVisible(false);
            courseForm.resetFields();
            fetchCourses();
          }
        });
      }
    } catch (error) {
      message.error('Failed to save training course');
    }
  };

  // Handle delete course
  const handleDeleteCourse = async (courseId) => {
    try {
      await delete_training_course(courseId, (res) => {
        if (res?.status === 'success') {
          message.success('Training course deleted successfully');
          fetchCourses();
        }
      });
    } catch (error) {
      message.error('Failed to delete training course');
    }
  };

  // Handle edit course
  const handleEditCourse = (course) => {
    setEditingCourse(course);
    courseForm.setFieldsValue({
      course_code: course.course_code,
      course_name: course.course_name,
      training_type: course.training_type,
      description: course.description,
      duration_hours: course.duration_hours,
      validity_months: course.validity_months,
      is_active: course.is_active,
      requires_assessment: course.requires_assessment,
      passing_score: course.passing_score,
      training_material_url: course.training_material_url,
    });
    setCourseModalVisible(true);
  };

  // Handle add operation requirement
  const handleSaveRequirement = async () => {
    try {
      const values = await requirementForm.validateFields();
      console.log('Form values:', values);

      // Validate that operations array is not empty
      if (!operations || operations.length === 0) {
        message.error('No operations available. Please refresh the page.');
        return;
      }

      // Find the selected operation to get operation_name
      const selectedOperation = operations.find((op) => op.operation_code === values.operation_code);
      console.log('Selected operation:', selectedOperation);

      if (!selectedOperation) {
        message.error('Selected operation not found. Please try again.');
        return;
      }

      // Prepare data with operation_name included
      const requestData = {
        operation_code: values.operation_code,
        course_id: values.course_id,
        operation_name: selectedOperation.operation_name || values.operation_code,
        is_mandatory: values.is_mandatory !== undefined ? values.is_mandatory : true,
        customer_id: 1,
      };

      console.log('Sending request data:', requestData);

      await create_operation_requirement(requestData, (res) => {
        console.log('API Response:', res);
        if (res?.status === 'success') {
          message.success(res.message || 'Operation requirement created successfully');
          setRequirementModalVisible(false);
          requirementForm.resetFields();
          fetchRequirements();
        } else {
          message.error(res?.message || 'Failed to create operation requirement');
        }
      });
    } catch (error) {
      console.error('Error saving requirement:', error);
      if (error.errorFields) {
        message.error('Please fill in all required fields');
      } else {
        message.error(error?.message || 'Failed to save operation requirement');
      }
    }
  };

  // Training Courses Table Columns
  const allCourseColumns = [
    {
      title: 'Course Code',
      dataIndex: 'course_code',
      key: 'course_code',
      width: '12%',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      searchable: true,
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: 'Course Name',
      dataIndex: 'course_name',
      key: 'course_name',
      width: '20%',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      searchable: true,
    },
    {
      title: 'Type',
      dataIndex: 'training_type',
      key: 'training_type',
      width: '12%',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      searchable: true,
      render: (type) => {
        const typeConfig = trainingTypes.find((t) => t.value === type);
        return (
          <Tag color="blue" icon={typeConfig?.icon}>
            {type}
          </Tag>
        );
      },
    },
    {
      title: 'Duration (hrs)',
      dataIndex: 'duration_hours',
      key: 'duration_hours',
      width: '10%',
      align: 'center',
      defaultVisible: true,
      sortable: true,
      sortType: 'number',
    },
    {
      title: 'Validity (months)',
      dataIndex: 'validity_months',
      key: 'validity_months',
      width: '12%',
      align: 'center',
      defaultVisible: true,
      sortable: true,
      sortType: 'number',
    },
    {
      title: 'Assessment',
      dataIndex: 'requires_assessment',
      key: 'requires_assessment',
      width: '10%',
      align: 'center',
      defaultVisible: false,
      sortable: true,
      sortType: 'boolean',
      render: (requires) => (
        <Tag color={requires ? 'green' : 'default'}>{requires ? 'Required' : 'Not Required'}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: '8%',
      align: 'center',
      defaultVisible: true,
      sortable: true,
      sortType: 'boolean',
      render: (active) => (
        <Tag color={active ? 'green' : 'red'}>{active ? 'Active' : 'Inactive'}</Tag>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: '10%',
      align: 'center',
      defaultVisible: true,
      sortable: false,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<Icons.Pencil className="text-amber-600 w-5 h-5 cursor-pointer hover:text-amber-800" />}
            size="small"
            onClick={() => handleEditCourse(record)}
          />
          <Popconfirm
            title="Are you sure you want to delete this course?"
            onConfirm={() => handleDeleteCourse(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<Icons.Trash2 className="text-red-600 w-5 h-5 cursor-pointer hover:text-red-800" />} size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Operation Requirements Table Columns
  const allRequirementColumns = [
    {
      title: 'Operation Code',
      dataIndex: 'operation_code',
      key: 'operation_code',
      width: '20%',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      searchable: true,
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: 'Operation Name',
      dataIndex: 'operation_name',
      key: 'operation_name',
      width: '25%',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      searchable: true,
    },
    {
      title: 'Required Course',
      dataIndex: 'course_name',
      key: 'course_name',
      width: '25%',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      searchable: true,
    },
    {
      title: 'Course Code',
      dataIndex: 'course_code',
      key: 'course_code',
      width: '15%',
      defaultVisible: false,
      sortable: true,
      sortType: 'string',
      searchable: true,
    },
    {
      title: 'Mandatory',
      dataIndex: 'is_mandatory',
      key: 'is_mandatory',
      width: '10%',
      align: 'center',
      defaultVisible: true,
      sortable: true,
      sortType: 'boolean',
      render: (mandatory) => (
        <Tag color={mandatory ? 'red' : 'blue'}>{mandatory ? 'Mandatory' : 'Optional'}</Tag>
      ),
    },
  ];

  // Table configuration hooks
  const {
    visibleColumns: visibleCourseColumns,
    filteredData: filteredCourseData,
    columnConfigMenu: courseColumnConfigMenu,
  } = useTableConfig({
    tableKey: 'training_courses',
    columns: allCourseColumns,
    data: courses || [],
    searchValue: courseSearchValue,
  });

  const {
    visibleColumns: visibleRequirementColumns,
    filteredData: filteredRequirementData,
    columnConfigMenu: requirementColumnConfigMenu,
  } = useTableConfig({
    tableKey: 'operation_requirements',
    columns: allRequirementColumns,
    data: requirements || [],
    searchValue: requirementSearchValue,
  });

  return (
    <div
      className="p-2.5 pt-5"
      style={{ backgroundColor: isDark ? defaultColors.palette.gray[900] : '#f9fafb' }}
    >
      <Card
        className="border border-gray-300 shadow-md"
        bordered={false}
        style={{
          backgroundColor: isDark ? defaultColors.palette.gray[800] : '#ffffff',
          minHeight: 'calc(100vh - 120px)',
        }}
        title={
          <div className="flex items-center gap-2">
            <Icons.BookOpen className="w-7 h-7 text-blue-600" />
            <Title level={4} style={{ color: textColor, margin: 0, padding: 0 }}>
              {t('Training Configuration')}
            </Title>
          </div>
        }
      >
      {/* Top Summary Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <StatCard
            label="Total Courses"
            value={stats.totalCourses}
            icon={<BookOpen size={24} />}
            bgColor="#dbeafe"
            iconColor="#3b82f6"
          />
        </Col>
        <Col span={8}>
          <StatCard
            label="Active Courses"
            value={stats.activeCourses}
            icon={<CheckCircle size={24} />}
            bgColor="#d1fae5"
            iconColor="#10b981"
          />
        </Col>
        <Col span={8}>
          <StatCard
            label="Operations w/ Requirements"
            value={stats.operationsWithRequirements}
            icon={<Settings size={24} />}
            bgColor="#f3e8ff"
            iconColor="#a855f7"
          />
        </Col>
      </Row>

      <Tabs defaultActiveKey="1">
        {/* Training Courses Tab */}
        <Tabs.TabPane
          tab={
            <span>
              <BookOutlined style={{ marginRight: 8 }} />
              Training Courses
            </span>
          }
          key="1"
        >
          <div
            style={{
              marginBottom: 16,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingCourse(null);
                courseForm.resetFields();
                setCourseModalVisible(true);
              }}
              style={{
                backgroundColor: isDark ? '#0F172A' : defaultColors.primary,
                borderColor: isDark ? '#0F172A' : defaultColors.primary,
              }}
            >
              Add Training Course
            </Button>
            <div className="flex items-center gap-3">
              <TableColumnConfig menu={courseColumnConfigMenu} />
              <ReusableSearchInput
                placeholder="Search courses..."
                width={250}
                mt={0}
                mb={0}
                onSearch={(value) => setCourseSearchValue(value)}
                onChange={(e) => setCourseSearchValue(e.target.value)}
              />
            </div>
          </div>
          <TableContainer
            className="training-courses-table"
            columns={visibleCourseColumns}
            data={filteredCourseData}
            rowKey="id"
            loading={coursesLoading}
            pagination={{ pageSize: 10, pageSizeOptions: ['10', '20', '50', '100'], showTotal: undefined }}
            size="small"
            bordered
            scrollX="max-content"
            height="calc(100vh - 500px)"
          />
          {/* Page-specific pagination-bar spacing — not something TableContainer covers generically. */}
          <style>
            {`
              .training-courses-table .ant-table-pagination {
                margin-top: 16px !important;
              }
            `}
          </style>
        </Tabs.TabPane>

        {/* Operation Requirements Tab */}
        <Tabs.TabPane
          tab={
            <span>
              <ToolOutlined style={{ marginRight: 8 }} />
              Operation Requirements
            </span>
          }
          key="2"
        >
          <div
            style={{
              marginBottom: 16,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                console.log('Opening modal. Operations count:', operations.length);
                console.log('Courses count:', courses.length);
                console.log('First 3 operations:', operations.slice(0, 3));
                requirementForm.resetFields();
                setRequirementModalVisible(true);
              }}
              style={{
                backgroundColor: isDark ? '#0F172A' : defaultColors.primary,
                borderColor: isDark ? '#0F172A' : defaultColors.primary,
              }}
            >
              Add Operation Requirement
            </Button>
            <div className="flex items-center gap-3">
              <TableColumnConfig menu={requirementColumnConfigMenu} />
              <ReusableSearchInput
                placeholder="Search requirements..."
                width={250}
                mt={0}
                mb={0}
                onSearch={(value) => setRequirementSearchValue(value)}
                onChange={(e) => setRequirementSearchValue(e.target.value)}
              />
            </div>
          </div>
          <TableContainer
            className="operation-requirements-table"
            columns={visibleRequirementColumns}
            data={filteredRequirementData}
            rowKey="id"
            loading={requirementsLoading}
            pagination={{ pageSize: 10, pageSizeOptions: ['10', '20', '50', '100'], showTotal: undefined }}
            size="small"
            bordered
            scrollX="max-content"
            height="calc(100vh - 480px)"
          />
          {/* Page-specific pagination-bar spacing — not something TableContainer covers generically. */}
          <style>
            {`
              .operation-requirements-table .ant-table-pagination {
                margin-top: 16px !important;
              }
            `}
          </style>
        </Tabs.TabPane>
      </Tabs>

      {/* Course Modal */}
      <Modal
        title={editingCourse ? 'Edit Training Course' : 'Create Training Course'}
        open={courseModalVisible}
        onOk={handleSaveCourse}
        onCancel={() => {
          setCourseModalVisible(false);
          courseForm.resetFields();
          setEditingCourse(null);
        }}
        width={700}
        okText={editingCourse ? 'Update' : 'Create'}
      >
        <Form form={courseForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="course_code"
                label="Course Code"
                rules={[{ required: true, message: 'Please enter course code' }]}
              >
                <Input placeholder="e.g., GMP-001" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="training_type"
                label="Training Type"
                rules={[{ required: true, message: 'Please select training type' }]}
              >
                <Select placeholder="Select training type">
                  {trainingTypes.map((type) => (
                    <Option key={type.value} value={type.value}>
                      {type.icon} {type.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="course_name"
            label="Course Name"
            rules={[{ required: true, message: 'Please enter course name' }]}
          >
            <Input placeholder="e.g., Good Manufacturing Practice Fundamentals" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <TextArea rows={3} placeholder="Brief description of the training course" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="duration_hours"
                label="Duration (hours)"
                rules={[{ required: true, message: 'Please enter duration' }]}
              >
                <InputNumber min={0.5} step={0.5} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="validity_months"
                label="Validity (months)"
                rules={[{ required: true, message: 'Please enter validity period' }]}
                initialValue={12}
              >
                <InputNumber min={1} max={60} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="passing_score" label="Passing Score (%)" initialValue={80}>
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="requires_assessment"
                label="Requires Assessment"
                valuePropName="checked"
                initialValue={true}
              >
                <Switch checkedChildren="Yes" unCheckedChildren="No" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="is_active"
                label="Active Status"
                valuePropName="checked"
                initialValue={true}
              >
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="training_material_url" label="Training Material URL">
            <Input placeholder="https://..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Operation Requirement Modal */}
      <Modal
        title="Add Operation Training Requirement"
        open={requirementModalVisible}
        onOk={handleSaveRequirement}
        onCancel={() => {
          setRequirementModalVisible(false);
          requirementForm.resetFields();
        }}
        width={600}
      >
        <Form form={requirementForm} layout="vertical">
          <Form.Item
            name="operation_code"
            label="Operation"
            rules={[{ required: true, message: 'Please select an operation' }]}
          >
            <Select
              placeholder={operations.length === 0 ? 'No operations available' : 'Select operation'}
              showSearch
              optionFilterProp="label"
              disabled={operations.length === 0}
            >
              {operations.map((op) => (
                <Option
                  key={op.id || op.operation_code}
                  value={op.operation_code}
                  label={op.operation_name}
                >
                  {op.operation_name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="course_id"
            label="Required Training Course"
            rules={[{ required: true, message: 'Please select a training course' }]}
          >
            <Select
              placeholder={courses.filter((c) => c.is_active).length === 0 ? 'No active courses available' : 'Select training course'}
              showSearch
              optionFilterProp="label"
              disabled={courses.filter((c) => c.is_active).length === 0}
            >
              {courses
                .filter((c) => c.is_active)
                .map((course) => (
                  <Option
                    key={course.id}
                    value={course.id}
                    label={`${course.course_code} - ${course.course_name}`}
                  >
                    {course.course_code} - {course.course_name}
                  </Option>
                ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="is_mandatory"
            label="Is Mandatory"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch checkedChildren="Mandatory" unCheckedChildren="Optional" />
          </Form.Item>
        </Form>
      </Modal>
      </Card>
    </div>
  );
};

export default TrainingConfiguration;
